import React, { useEffect, useState } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import MonthYearPicker from "./MonthYearPicker";
import { PlusIcon, XMarkIcon, PencilIcon } from "@heroicons/react/24/outline";
import SearchableDropdown from "./SearchableDropdown";

/* ── shared input style — matches AdminAttendanceOverview ────────────────── */
const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

/* ── status badge ────────────────────────────────────────────────────────── */
const getStatusStyles = (status) => {
  switch (status?.toLowerCase()) {
    case "approved": return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    case "pending":  return "bg-amber-50 text-amber-700 border border-amber-200";
    case "rejected": return "bg-rose-50 text-rose-700 border border-rose-200";
    default:         return "bg-slate-100 text-slate-600 border border-slate-200";
  }
};

/* ════════════════════════════════════════════════════════════════════════════ */
const ExpenseModal = ({ isOpen, onClose, employee }) => {
  const [expenseList, setExpenseList]             = useState([]);
  const [month, setMonth]                         = useState(new Date().getMonth() + 1);
  const [year, setYear]                           = useState(new Date().getFullYear());
  const [loading, setLoading]                     = useState(false);
  const [showForm, setShowForm]                   = useState(false);
  const [expenseTypes, setExpenseTypes]           = useState([]);
  const [availableYears, setAvailableYears]       = useState([]);
  const [isEdit, setIsEdit]                       = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  const [form, setForm] = useState({
    date: "", expense_type: null, location: "",
    amount: "", purpose: "", about: "",
  });

  const handleRowClick = (exp) => {
    console.log("Clicked expense:", exp);
    setIsEdit(true);
    setSelectedExpenseId(exp.sl);
    setForm({
      date: exp.date, expense_type: exp.type,
      location: exp.location || "", amount: exp.amount,
      purpose: exp.purpose, about: exp.about || "",
    });
    setShowForm(true);
  };

  const fetchExpenseFilters = async () => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/expenseHistory.php`, { employee_id: employee.employee_id });
      if (res.data.status === "success") {
        const filters     = res.data.filters;
        const uniqueYears = [...new Set(filters.map(f => f.year))];
        setAvailableYears(uniqueYears);
        if (filters.length > 0) {
          setMonth(filters[0].month);
          setYear(filters[0].year);
        }
      }
    } catch (err) { console.error("Filter fetch error:", err); }
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${SERVER_URL}/Rahul/expenseHistory.php`, { employee_id: employee.employee_id, month, year });
      setExpenseList(res.data.status === "success" ? res.data.data || [] : []);
    } catch (err) { console.error("Expense fetch error:", err); }
    finally { setLoading(false); }
  };

  const fetchExpenseTypes = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/get_expense_type.php`);
      if (res.data.status === "success") setExpenseTypes(res.data.data || []);
    } catch (err) { console.error("Type fetch error:", err); }
  };

  const submitExpense = async () => {
    if (!form.expense_type) { alert("Please select expense type"); return; }
    const payload = {
      employee_id:  employee.employee_id,
      date:         form.date,
      expense_type: form.expense_type,
      location:     form.location,
      amount:       form.amount,
      purpose:      form.purpose,
      about:        form.about,
    };
    try {
      let res;
      console.log("Submitting update with ID:", selectedExpenseId);
      if (isEdit) {
        res = await axios.post(`${SERVER_URL}/Rahul/updateExpense.php`, { ...payload, id: selectedExpenseId });
      } else {
        res = await axios.post(`${SERVER_URL}/Rahul/insertNewExpense.php`, payload);
      }
      if (res.data.status === "success") {
        alert(isEdit ? "Expense updated" : "Expense added");
        resetFormState();
        fetchExpenses();
      } else { alert(res.data.message || "Failed"); }
    } catch (err) { console.error(err); alert("Something went wrong"); }
  };

  useEffect(() => {
    if (isOpen) { fetchExpenseFilters(); fetchExpenses(); fetchExpenseTypes(); }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && month && year) fetchExpenses();
  }, [month, year]);

  if (!isOpen) return null;

  const expenseTypeOptions = expenseTypes.map(t => ({ label: t.expense_name, value: t.expense_name }));

  const resetFormState = () => {
    setShowForm(false);
    setIsEdit(false);
    setSelectedExpenseId(null);
    setForm({ date: "", expense_type: null, location: "", amount: "", purpose: "", about: "" });
  };

  const minYear = availableYears.length > 0 ? Math.min(...availableYears) : new Date().getFullYear() - 5;
  const maxYear = new Date().getFullYear();

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .exp-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .exp-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .exp-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .exp-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}
      >
        <div
          className="w-[540px] max-h-[90vh] rounded-xl bg-white border border-slate-200 flex flex-col overflow-hidden"
          style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
        >

          {/* HEADER */}
          <div
            className="px-6 py-4 border-b border-slate-200 flex justify-between items-center shrink-0"
            style={{ background: "#f8fafc" }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-1 h-5 rounded-full"
                style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
              />
              {showForm
                ? isEdit
                  ? <PencilIcon className="w-4 h-4 text-blue-700" />
                  : <PlusIcon className="w-4 h-4 text-blue-700" />
                : null
              }
              <h2 className="text-sm font-bold text-slate-800 m-0 tracking-tight">
                {showForm
                  ? isEdit ? "Edit Expense Claim" : "New Expense Claim"
                  : "Expense Claims"
                }
              </h2>
            </div>
            <button
              onClick={() => { resetFormState(); onClose(); }}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 min-h-0 flex flex-col">

            {/* ── LIST VIEW ── */}
            {!showForm && (
              <>
                {/* Filter bar */}
                <div className="flex gap-3 p-4 border-b border-slate-200 shrink-0 items-center bg-white">
                  <MonthYearPicker
                    selectedMonth={month}
                    selectedYear={year}
                    minYear={minYear}
                    maxYear={maxYear}
                    onChange={({ month: m, year: y }) => {
                      if (m && y) { setMonth(m); setYear(y); }
                    }}
                    label="Select Month"
                  />
                  <button
                    className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors shrink-0"
                    onClick={() => {
                      setIsEdit(false);
                      setSelectedExpenseId(null);
                      setForm({ date: "", expense_type: null, location: "", amount: "", purpose: "", about: "" });
                      setShowForm(true);
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Expense
                  </button>
                </div>

                {/* Table */}
                <div className="flex-1 min-h-0 overflow-y-auto exp-scrollbar" style={{ maxHeight: "360px" }}>
                  <table className="w-full table-fixed text-sm border-collapse">
                    <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                      <tr>
                        {["Date", "Type", "Purpose", "Amount", "Approved", "Status"].map((h) => (
                          <th key={h} className="py-3 px-3 text-left border-b border-slate-200">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center p-8">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              <span className="text-xs text-slate-400">Loading…</span>
                            </div>
                          </td>
                        </tr>
                      ) : expenseList.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center p-8 text-slate-400 text-sm">No records found</td>
                        </tr>
                      ) : (
                        expenseList.map((exp, i) => (
                          <tr
                            key={i}
                            onClick={() => handleRowClick(exp)}
                            className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60"
                          >
                            <td className="py-3 px-3 text-slate-500 text-xs truncate">{exp.date}</td>
                            <td className="py-3 px-3 text-slate-600 text-xs truncate">{exp.type}</td>
                            <td className="py-3 px-3 text-slate-700 text-xs truncate">{exp.purpose}</td>
                            <td className="py-3 px-3 text-slate-700 text-xs">₹ {exp.amount}</td>
                            <td className="py-3 px-3 text-slate-500 text-xs">₹ {exp.paid_amount || "—"}</td>
                            <td className="py-3 px-3">
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyles(exp.status)}`}>
                                {exp.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── FORM VIEW ── */}
            {showForm && (
              <div className="flex-1 min-h-0 overflow-y-auto exp-scrollbar p-6">
                <div className="grid grid-cols-2 gap-4">

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Date</label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={e => setForm({ ...form, date: e.target.value })}
                      className={inputStyle}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Expense Type</label>
                    <SearchableDropdown
                      options={expenseTypeOptions}
                      value={form.expense_type}
                      onChange={opt => setForm({ ...form, expense_type: opt })}
                      placeholder="Expense Type"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Location</label>
                    <input
                      placeholder="Location"
                      value={form.location}
                      onChange={e => setForm({ ...form, location: e.target.value })}
                      className={inputStyle}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Amount</label>
                    <input
                      type="number"
                      placeholder="Amount"
                      value={form.amount}
                      onChange={e => setForm({ ...form, amount: e.target.value })}
                      className={inputStyle}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Purpose</label>
                    <input
                      placeholder="Purpose"
                      value={form.purpose}
                      onChange={e => setForm({ ...form, purpose: e.target.value })}
                      className={inputStyle}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">About</label>
                    <textarea
                      placeholder="About"
                      value={form.about}
                      onChange={e => setForm({ ...form, about: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors resize-none h-24"
                    />
                  </div>

                </div>

                <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-slate-200">
                  <button
                    onClick={resetFormState}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitExpense}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all"
                    style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                  >
                    {isEdit && <PencilIcon className="h-4 w-4" />}
                    {isEdit ? "Update" : "Submit"}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default ExpenseModal;